/**
 * Agent Factory (ADR-0012) — published agent definitions (typed DAGs) and the
 * tool registry. Write tools are human-approval gated; agent runs execute behind
 * the AI Gateway choke point. Admin/All-scoped (child of the Intelligence nav item).
 */
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useShellSlot } from '../components/shellContext';
import {
  type AgentDefinition,
  type ToolSpec,
  useAgentDefinitions,
  useAgentTools,
} from '../hooks/useAgentPlatform';

export function AgentFactoryPage() {
  useShellSlot({ breadcrumb: 'Agent Factory' });
  const agents = useAgentDefinitions();
  const tools = useAgentTools();

  const agentCols: Column<AgentDefinition>[] = [
    { key: 'name', header: 'Agent', render: (a) => <strong>{a.name}</strong> },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <StatusBadge status={a.status === 'published' ? 'success' : 'neutral'} label={a.status} />
      ),
    },
    { key: 'version', header: 'Version', align: 'right', render: (a) => `v${a.version}` },
    {
      key: 'nodes',
      header: 'Steps',
      render: (a) => (a.graph?.nodes ?? []).map((n) => n.type).join(' → '),
    },
  ];

  const toolCols: Column<ToolSpec>[] = [
    { key: 'name', header: 'Tool', render: (t) => <strong>{t.name}</strong> },
    { key: 'service', header: 'Service', render: (t) => t.target_service ?? '—' },
    {
      key: 'side_effect',
      header: 'Side effect',
      render: (t) => (
        <StatusBadge status={t.side_effect === 'write' ? 'warning' : 'info'} label={t.side_effect} />
      ),
    },
    {
      key: 'gated',
      header: 'HITL gated?',
      render: (t) =>
        t.requires_approval ? (
          <StatusBadge status="warning" label="approval required" />
        ) : (
          <StatusBadge status="neutral" label="inline" />
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Agent Factory"
        subtitle="Published agent definitions (typed DAGs) run behind the AI Gateway. Write tools are human-approval gated."
        actions={
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              agents.refresh();
              tools.refresh();
            }}
            disabled={agents.isLoading || tools.isLoading}
          >
            Refresh
          </button>
        }
      />

      <Card title="Agent definitions" padded={false} style={{ marginBottom: 16 }}>
        {agents.isLoading && !agents.items.length ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading agents…" />
          </div>
        ) : (
          <DataTable
            columns={agentCols}
            rows={agents.items}
            rowKey={(a) => a.id}
            emptyTitle="No agents"
            emptyDescription="Author and publish agent definitions to run advisory workflows."
          />
        )}
      </Card>

      <Card title="Tool registry" padded={false}>
        {tools.isLoading && !tools.items.length ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading tools…" />
          </div>
        ) : (
          <DataTable
            columns={toolCols}
            rows={tools.items}
            rowKey={(t) => t.name}
            emptyTitle="No tools"
            emptyDescription="Tools the orchestrator can invoke; write tools require human approval."
          />
        )}
      </Card>
    </>
  );
}
